import type { PermissionEnum } from '@Application/Security/Roles';
import type { PermissionPayload } from '@Application/Security/Roles/Actions/userPermissionsFetched';
import UpdateRole from '@Application/Security/Roles/Commands/UpdateRole';
import updateRole from '@Application/Security/Roles/updateRole';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { setPermissionToRevoke } from '@UI/Pages/Administration/Roles/Signals/permissionToRevokeSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import { css } from '@emotion/react';
import { Check } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';

const rowStyle = css`
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	width: 100%;
	border: none;
	background: none;
	padding: 0.625rem;
	border-radius: ${theme.radii.medium};
	cursor: pointer;
	text-align: left;
	font: inherit;
	color: inherit;

	&:hover:not(:disabled) {
		background-color: ${theme.colors.surfaceMuted};
	}

	&:disabled {
		cursor: default;
	}
`;

const boxUngrantedStyle = css`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.375rem;
	height: 1.375rem;
	margin-top: 0.125rem;
	border-radius: ${theme.radii.xsmall};
	border: 1.5px solid ${theme.colors.borderStrong};
	background-color: ${theme.colors.surfaceInset};
`;

const boxGrantedStyle = css`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.375rem;
	height: 1.375rem;
	margin-top: 0.125rem;
	border-radius: ${theme.radii.xsmall};
	border: 1.5px solid ${theme.colors.accent};
	background-color: ${theme.colors.accent};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const labelStyle = css`
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.medium};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};
`;

const descriptionStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	color: ${theme.colors.inkFaint};
`;

type Props = {
	readonly role: RoleView;
	readonly permission: PermissionPayload;
};

/**
 * A single toggleable permission line. Grants and revokes from a role nobody carries apply
 * immediately; revoking from a role carried by users opens the confirmation instead of mutating —
 * `PermissionRow` never dispatches in that branch.
 */
const PermissionRow: React.FC<Props> = ({ role, permission }) => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const isGranted = role.hasPermission(permission.value);

	const handleToggle = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		const permissionValue = permission.value as PermissionEnum;

		if (isGranted && role.assignedUserCount > 0) {
			setPermissionToRevoke({ roleId: role.id, permission: permissionValue });
			return;
		}

		const nextPermissions = isGranted
			? role.permissions.filter((value) => value !== permission.value)
			: [...role.permissions, permission.value];

		dispatch(
			updateRole(
				new UpdateRole(
					accountId,
					role.id,
					role.label,
					role.description,
					nextPermissions as readonly PermissionEnum[],
				),
			),
		).catch(ignoreHandledError);
	}, [accountId, dispatch, isGranted, permission.value, role]);

	return (
		<button
			type="button"
			aria-pressed={isGranted}
			css={rowStyle}
			onClick={handleToggle}
			disabled={role.isSystem}
		>
			<span css={isGranted ? boxGrantedStyle : boxUngrantedStyle}>
				{isGranted ? <Check size={13} color={theme.colors.surface} /> : null}
			</span>
			<span css={textStyle}>
				<span css={labelStyle}>{permission.label}</span>
				{permission.description !== '' ? (
					<span css={descriptionStyle}>{permission.description}</span>
				) : null}
			</span>
		</button>
	);
};

export default PermissionRow;
